import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const BLOCK_SIZE = 16
const CHANNEL_START = 0x0200
const CHANNEL_SIZE = 32
const CHANNEL_COUNT = 100
const USED_START = 0x0120
const SKIP_START = 0x0130
const ALINCO_TONES = CTCSS_TONES.filter(
  (tone) => ![159.8, 165.5, 171.3, 177.3, 183.5, 189.9, 196.6, 199.5, 206.5, 229.1, 254.1].includes(tone),
)

type AlincoSpec = {
  id: string
  vendor: 'Alinco' | 'Jetstream'
  model: string
  summary: string
  command: string
  memorySize: number
  powerLevels: readonly string[]
  recordUsed?: boolean
}

export type AlincoDRx35Profile = {
  protocol: 'alinco-drx35'
  id: string
  vendor: 'Alinco' | 'Jetstream'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 0
  channelMaximum: 99
  nameLength: 7
  memorySize: number
  command: readonly number[]
  powerLevels: readonly string[]
  recordUsed: boolean
}

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

const SPECS: readonly AlincoSpec[] = [
  { id: 'alinco-dr03t', vendor: 'Alinco', model: 'DR03T', summary: 'Alinco DR03T 10-meter mobile.', command: 'DR135', memorySize: 4096, powerLevels: ['High'] },
  { id: 'alinco-dr06t', vendor: 'Alinco', model: 'DR06T', summary: 'Alinco DR06T 6-meter mobile.', command: 'DR435', memorySize: 4096, powerLevels: ['High'] },
  { id: 'alinco-dr135t', vendor: 'Alinco', model: 'DR135T', summary: 'Alinco DR135T VHF mobile.', command: 'DR135', memorySize: 4096, powerLevels: ['High'] },
  { id: 'alinco-dr235t', vendor: 'Alinco', model: 'DR235T', summary: 'Alinco DR235T 1.25-meter mobile.', command: 'DR235', memorySize: 4096, powerLevels: ['High'] },
  { id: 'alinco-dr435t', vendor: 'Alinco', model: 'DR435T', summary: 'Alinco DR435T UHF mobile.', command: 'DR435', memorySize: 4096, powerLevels: ['High'] },
  { id: 'alinco-dj596', vendor: 'Alinco', model: 'DJ596', summary: 'Alinco DJ596 dual-band mobile.', command: 'DJ596', memorySize: 4096, powerLevels: ['Low', 'High'] },
  { id: 'jetstream-jt220m', vendor: 'Jetstream', model: 'JT220M', summary: 'Jetstream JT220M 1.25-meter mobile.', command: 'DR136', memorySize: 8192, powerLevels: ['High'], recordUsed: true },
  { id: 'alinco-dj175', vendor: 'Alinco', model: 'DJ175', summary: 'Alinco DJ175 dual-band mobile.', command: 'DJ175', memorySize: 6896, powerLevels: ['Low', 'Mid', 'High'], recordUsed: true },
]

export const ALINCO_DRX35_PROFILES: readonly AlincoDRx35Profile[] = SPECS.map(
  (spec) => ({
    protocol: 'alinco-drx35',
    id: spec.id,
    vendor: spec.vendor,
    model: spec.model,
    summary: spec.summary,
    tags: ['Experimental', 'Read + write', '100 channels', '7-character names'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 99,
    nameLength: 7,
    memorySize: spec.memorySize,
    command: ascii(spec.command),
    powerLevels: spec.powerLevels,
    recordUsed: spec.recordUsed ?? false,
  }),
)

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'High',
    skip: false,
  }
}

function decodeBcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  return value
}

function encodeBcd(value: number, size: number): Uint8Array {
  const result = new Uint8Array(size)
  for (let index = size - 1; index >= 0; index -= 1) {
    const part = value % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  return result
}

function usedBit(image: Uint8Array, number: number): boolean {
  return (image[USED_START + Math.floor(number / 8)] & (0x80 >> (number % 8))) !== 0
}

function setUsedBit(image: Uint8Array, number: number, used: boolean): void {
  const offset = USED_START + Math.floor(number / 8)
  const bit = 0x80 >> (number % 8)
  image[offset] = used ? image[offset] | bit : image[offset] & ~bit
}

function skipBit(image: Uint8Array, number: number): boolean {
  return (image[SKIP_START + Math.floor(number / 8)] & (0x80 >> (number % 8))) !== 0
}

function setSkipBit(image: Uint8Array, number: number, skip: boolean): void {
  const offset = SKIP_START + Math.floor(number / 8)
  const bit = 0x80 >> (number % 8)
  image[offset] = skip ? image[offset] | bit : image[offset] & ~bit
}

function decodeName(raw: Uint8Array): string {
  let end = raw.findIndex((value) => value === 0 || value === 0xff)
  if (end < 0) end = raw.length
  return Array.from(raw.slice(0, end))
    .map((value) =>
      value >= 0x30 && value <= 0x39
        ? String.fromCharCode(value)
        : value >= 0x41 && value <= 0x5a
          ? String.fromCharCode(value)
          : value === 0x3a
            ? ' '
            : '',
    )
    .join('')
    .trim()
}

export function parseAlincoDRx35Channel(
  image: Uint8Array,
  profile: AlincoDRx35Profile,
  number: number,
): WebChannel {
  const offset = CHANNEL_START + number * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE) return emptyChannel(number)
  const used = profile.recordUsed ? (raw[0] & 0x80) !== 0 : usedBit(image, number)
  if (!used && profile.model !== 'JT220M') return emptyChannel(number)
  const rxFrequency = decodeBcd(raw.slice(4, 8)) * 100
  if (!rxFrequency) return emptyChannel(number)
  const duplex = (raw[1] >> 6) & 0x03
  const shift = decodeBcd(raw.slice(9, 12)) * 100
  const txFrequency =
    duplex === 1
      ? rxFrequency + shift
      : duplex === 2
        ? rxFrequency - shift
        : rxFrequency
  const tmode = (raw[2] >> 4) & 0x0f
  const toneMode = tmode === 1 || tmode === 3 ? 'ctcss' : tmode === 12 ? 'dtcs' : 'none'
  const ctcss = ALINCO_TONES[(tmode === 3 ? raw[13] : raw[12])] ?? null
  const dtcs =
    profile.vendor === 'Jetstream'
      ? [17, ...DTCS_CODES][raw[14]] ?? null
      : DTCS_CODES[raw[14]] ?? null
  let power = profile.powerLevels[0]
  if (profile.model === 'DJ175') power = profile.powerLevels[raw[25] >> 6] ?? profile.powerLevels[0]
  else if (profile.powerLevels.length > 1) power = raw[0] & 0x10 ? profile.powerLevels.at(-1) ?? power : profile.powerLevels[0]
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(16, 23)),
    rxFrequency,
    txFrequency,
    toneMode,
    ctcss: toneMode === 'ctcss' ? ctcss : null,
    dtcs: toneMode === 'dtcs' ? dtcs : null,
    dtcsPolarity: 'N',
    mode: raw[0] & 0x04 ? 'NFM' : 'FM',
    power,
    skip: skipBit(image, number),
  }
}

export function parseAlincoDRx35Channels(
  image: Uint8Array,
  profile: AlincoDRx35Profile,
): WebChannel[] {
  return Array.from(
    { length: CHANNEL_COUNT },
    (_, number) => parseAlincoDRx35Channel(image, profile, number),
  )
}

export function applyAlincoDRx35Channel(
  image: Uint8Array,
  profile: AlincoDRx35Profile,
  channel: WebChannel,
): void {
  if (channel.number < 0 || channel.number >= CHANNEL_COUNT) {
    throw new Error('Channel must be between 0 and 99.')
  }
  if (profile.recordUsed) {
    const raw = image.subarray(
      CHANNEL_START + channel.number * CHANNEL_SIZE,
      CHANNEL_START + (channel.number + 1) * CHANNEL_SIZE,
    )
    raw[0] = channel.empty ? 0 : raw[0] | 0x80
  } else {
    setUsedBit(image, channel.number, !channel.empty)
  }
  setSkipBit(image, channel.number, !channel.empty && channel.skip)
  if (channel.empty) return
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  const offset = CHANNEL_START + channel.number * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  raw.fill(0)
  if (profile.recordUsed) raw[0] = 0x80
  const delta = channel.txFrequency ? channel.txFrequency - channel.rxFrequency : 0
  raw[1] = delta > 0 ? 0x40 : delta < 0 ? 0x80 : 0
  raw.set(encodeBcd(Math.round(channel.rxFrequency / 100), 4), 4)
  raw.set(encodeBcd(Math.round(Math.abs(delta) / 100), 3), 9)
  if (channel.mode === 'NFM') raw[0] |= 0x04
  const toneIndex = channel.ctcss
    ? (ALINCO_TONES as readonly number[]).indexOf(channel.ctcss)
    : -1
  if (channel.toneMode === 'ctcss' && toneIndex >= 0) {
    raw[2] = 0x10
    raw[12] = toneIndex
    raw[13] = toneIndex
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const codes = profile.vendor === 'Jetstream' ? [17, ...DTCS_CODES] : DTCS_CODES
    const index = (codes as readonly number[]).indexOf(channel.dtcs)
    if (index < 0) throw new Error('That DCS code is not supported by this radio.')
    raw[2] = 0xc0
    raw[14] = index
    raw[15] = index
  }
  if (profile.model === 'DJ175') {
    const power = Math.max(0, profile.powerLevels.indexOf(channel.power))
    raw[25] = (power & 0x03) << 6
  } else if (profile.powerLevels.length > 1 && channel.power === profile.powerLevels.at(-1)) {
    raw[0] |= 0x10
  }
  const name = channel.name.slice(0, 7).padEnd(7, ' ')
  for (let index = 0; index < 7; index += 1) raw[16 + index] = name.charCodeAt(index)
}

async function sendEcho(
  transport: RadioTransport,
  data: Uint8Array,
): Promise<void> {
  await transport.write(data)
  await transport.readExact(data.length, 3000)
}

function command(address: number, operation: 'R' | 'W', payload = ''): Uint8Array {
  return new TextEncoder().encode(
    `AL~F${address.toString(16).toUpperCase().padStart(4, '0')}${operation}${payload}\r\n`,
  )
}

async function identify(
  profile: AlincoDRx35Profile,
  transport: RadioTransport,
): Promise<void> {
  const cmd = new TextEncoder().encode(
    `${String.fromCharCode(...profile.command)}\r\n`,
  )
  await sendEcho(transport, cmd)
  const response = await transport.readExact(6, 3000)
  if (!new TextDecoder().decode(response).includes('OK')) {
    throw new Error(`The radio did not identify as ${profile.model}.`)
  }
}

async function exitProgramming(transport: RadioTransport): Promise<void> {
  await sendEcho(transport, new TextEncoder().encode('AL~E\r\n'))
  await transport.readExact(20, 1000)
}

function hexToBytes(value: string): Uint8Array {
  return Uint8Array.from(
    { length: value.length / 2 },
    (_, index) => Number.parseInt(value.slice(index * 2, index * 2 + 2), 16),
  )
}

export async function readAlincoDRx35RadioWithTransport(
  profile: AlincoDRx35Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await transport.discardInput()
  await identify(profile, transport)
  try {
    const image = new Uint8Array(profile.memorySize)
    const total = profile.memorySize / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      await sendEcho(transport, command(address, 'R'))
      const response = new TextDecoder().decode(
        await transport.readExact(41, 4000),
      ).trim()
      const data = hexToBytes(response.slice(response.indexOf(':') + 1))
      if (data.byteLength !== BLOCK_SIZE) {
        throw new Error(`Invalid Alinco block at 0x${address.toString(16)}.`)
      }
      image.set(data, address)
      onProgress?.({ current: index + 1, total, label: 'Reading Alinco memory' })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await exitProgramming(transport)
  }
}

export async function writeAlincoDRx35RadioWithTransport(
  profile: AlincoDRx35Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than Alinco memory.')
  }
  await transport.discardInput()
  await identify(profile, transport)
  try {
    const addresses = Array.from(
      { length: (profile.memorySize - 0x100) / BLOCK_SIZE },
      (_, index) => 0x100 + index * BLOCK_SIZE,
    )
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      const payload = Array.from(image.slice(address, address + BLOCK_SIZE))
        .map((value) => value.toString(16).toUpperCase().padStart(2, '0'))
        .join('')
      await sendEcho(transport, command(address, 'W', payload))
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Writing Alinco memory',
      })
    }
  } finally {
    await exitProgramming(transport)
  }
}
